import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDispatch, useSelector } from "react-redux"
import { incrementByAmount, decrementByAmount, reset } from "../../../store/reducers/loader"

import {
  IconButton,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material"

import {
  CloseRounded,
  UnfoldLessRounded,
  UnfoldMoreRounded
} from "@mui/icons-material"

import {
  FeatureGroup,
  GeoJSON,
  MapContainer,
  LayersControl,
  Pane,
  ScaleControl,
  ZoomControl,
  WMSTileLayer,
  Popup,
  TileLayer
} from "react-leaflet"

// import "leaflet.markercluster"
// import "leaflet.markercluster/dist/MarkerCluster.Default.css"

import "../utils/attribution"

import L from "leaflet"

import "leaflet/dist/leaflet.css"
import "./HKVMap.css"

import { capitalizeFirstLetter, describeResponseBody, makeRequest } from "../../../lib/utils"

const { BaseLayer, Overlay } = LayersControl

import { createRoot } from "react-dom/client"

import { getArcGISAPIKey } from "../../../store/environment"
import { catchMessage } from "../../../store/reducers/errors"
import ResetBoundsControl from "./ResetBoundsControl"
import { createTimeDimensionLayer } from "../utils/functions"

function HKVMap(props) {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const dispatch = useDispatch()

  const ArcGISApiKey = getArcGISAPIKey()
  const accountinfo = useSelector(state => state.accountinfo)

  const bounds = L.latLngBounds(
    L.latLng(-40.39617972504841, -22.14393537183129), // southWest
    L.latLng(46.80535088596816, 51.936548500915606) // northEast
  )
  const maxBounds = L.latLngBounds(
    L.latLng(-57.51582286553883, -116.3671875),
    L.latLng(61.93895042666063, 108.984375)
  )

  const {
    config,
    layersToFetch,
    mapClick,
    mapMount,
    mapFeatureClick,
    mapFeatureStyle,
    mapFeatureOptions,
    mapRemoveFeatureGroup,
    selectedFeatureId,
    view
  } = props

  const [mapRef, setMapRef] = useState()
  const [groupRefs, setGroupRefs] = useState({})
  const [panes, setPanes] = useState([{ key: "selected", zIndex: 599 }, { key: "highlight", zIndex: 600 }])
  const [hovered, setHovered] = useState()
  const [selected, setSelected] = useState()

  const [fetchedLayers, setFetchedLayers] = useState([])

  const [selectedLayer, setSelectedLayer] = useState()
  const popupElRef = useRef()

  const [minimized, setMinimized] = useState(false)
  const minimizedRef = useRef(minimized)

  const [docked, setDocked] = useState(false)
  const dockedRef = useRef(docked)

  const drawControlRef = useRef()
  const geoJsonRefs = useRef({})

  const prefix = `<a href='https://leafletjs.com' target='_blank'>Leaflet</a> | <a href='https://www.esri.com/en-us/home' target='_blank'>ESRI</a> | <a href='https://www.hkv.nl/en' target='_blank'>HKV Services</a>`
  const attribution = L.control.attribution({ position: "bottomright", prefix: prefix })

  // claude: één controller voor de hele levensduur van de kaart (HSGA-204). Voorheen werd per render een
  // nieuwe controller gemaakt en in de cleanup van het config.layers-effect afgebroken — dus bij ELKE wijziging
  // van de kaartlagen (bv. als Rainsat na het ophalen van de FEWS-data de bassin/flood-lagen toevoegt) werden
  // lopende WFS-requests afgebroken. Gevolg: pop-up "Request aborted (timeout)" en een laag zonder features,
  // vooral op trage verbindingen. Nu alleen afbreken bij unmount.
  const controllerRef = useRef(new AbortController())
  // Lagen waarvan de features nog onderweg zijn, zodat een nieuwe effect-run ze niet dubbel ophaalt
  const fetchingKeysRef = useRef(new Set())

  useEffect(() => {
    return () => {
      controllerRef.current.abort()
      dispatch(reset())
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    const unfetchedLayers = config.layers.filter(
      layer => !fetchedLayers.some(f => f.key === layer.key) && !fetchingKeysRef.current.has(layer.key)
    )

    if (!unfetchedLayers.length) return

    unfetchedLayers.forEach(layer => {
      fetchingKeysRef.current.add(layer.key)
      fetchFeatures(layer, () => {
        fetchingKeysRef.current.delete(layer.key)
        setFetchedLayers(prevState => {
          if (prevState.some(p => p.key === layer.key)) {
            return prevState
          }
          return [...prevState, layer]
        })
      })
    })
  }, [config.layers])

  useEffect(() => {
    const layersConcat = (config.timeDimensionLayers ? config.timeDimensionLayers : []).concat(config.layers)
    layersConcat.forEach(layer => {
      const paneKey = layer.pane ? layer.pane : `${layer.key}Pane`

      setPanes(prevState => {
        if (prevState.some(pane => pane.key === paneKey)) {
          return prevState
        }

        return [
          ...prevState,
          {
            key: paneKey,
            zIndex: layer.zIndex ?? 400
          },
          ...(layer.cluster
            ? [{
              key: `clusterPolygon-${paneKey}`,
              zIndex: (layer.zIndex ?? 400) - 1
            }]
            : [])
        ]
      })
    })
  }, [config.layers?.length, config.timeDimensionLayers?.length])

  useEffect(() => {
    if (!layersToFetch?.length) return

    layersToFetch.forEach(layer => {
      fetchFeatures(layer, () => {
        setFetchedLayers(prevState => {
          if (prevState.some(p => p.key === layer.key)) {
            return prevState
          }

          return [...prevState, layer]
        })
      })
    })
  }, [layersToFetch])

  useEffect(() => {
    if (selected?.layer?.key === "basins" && !selectedFeatureId) {
      setSelected(null)
    }
  }, [selectedFeatureId]) // eslint-disable-line

  useEffect(() => {
    if (mapRef) {
      mapRef.eachLayer(layer => {
        if (layer.getElement) {
          const el = layer.getElement()
          if (el) {
            if (selectedLayer?._leaflet_id === layer._leaflet_id) {
              if (!el.classList.contains("highlighted-feature")) {
                el.classList.add("highlighted-feature")
              }
            } else {
              el.classList.remove("highlighted-feature")
            }
          }
        }
      })
    }
    props.setSelectedLayer && props.setSelectedLayer(selectedLayer)
  }, [selectedLayer])

  const checkMinimizedStyling = () => {
    const minimizedEl = document.getElementById("minimized")
    const maximizedEl = document.getElementById("maximized")
    const popupContentEl = document.getElementsByClassName("popup-content")

    if (popupContentEl?.length > 0) {
      Array.from(popupContentEl).forEach(el => {
        el.style.display = minimizedRef.current ? "none" : "block"
      })
    }

    if (minimizedEl) {
      minimizedEl.style.display = minimizedRef.current ? "block" : "none"
    }
    if (maximizedEl) {
      maximizedEl.style.display = !minimizedRef.current ? "block" : "none"
    }
  }

  useEffect(() => {
    minimizedRef.current = minimized
    checkMinimizedStyling()
  }, [minimized])

  useEffect(() => {
    dockedRef.current = docked
  }, [docked])

  useEffect(() => {
    const el = document.getElementsByClassName("leaflet-popup")

    const popupEl = popupElRef.current?._container // Leaflet popup container element
    if (!popupEl) return

    const mainContainer = document.getElementById("main")
    if (!mainContainer) return

    if (el?.length > 0) {
      Array.from(el).forEach(e => {
        if (docked) {
          e.classList.add("docked")
          e.classList.add("leaflet-left")

          const layer = config.layers.find(ly => ly.key === selectedLayer?.key)

          mainContainer.appendChild(popupEl)

          const getMoveStyle = () => {
            const drawerEl = document.getElementById("drawer-left")

            const offset = drawerEl && drawerEl.style.visibility !== "hidden"
              ? drawerEl.offsetWidth
              : 0

            return {
              marginTop: `${layer?.popup?.offset?.top || 0}px`,
              marginLeft: `${offset + (layer?.popup?.offset?.left || 0)}px`,
              marginRight: "unset"
            }
          }

          Object.assign(e.style, getMoveStyle())
        } else {
          const popupPane = mapRef.getPanes().popupPane
          popupPane.appendChild(popupEl)

          e.classList.remove("docked")
          e.classList.remove("leaflet-left")

          Object.assign(e.style, { marginTop: "", marginLeft: "", marginRight: "" })
        }
      })
    }
  }, [docked, popupElRef.current?.open, selectedLayer])

  useEffect(() => {
    Object.values(groupRefs).forEach((gRef) => {
      if (!gRef) return

      const target = gRef.clusterGroupRef || gRef
      if (typeof target.eachLayer !== "function") return

      const layerConfig = config.layers?.find(ly => ly.key === target.key)
      if (!layerConfig?.tooltip) return

      target.eachLayer((featureLayer) => {
        if (!featureLayer.getTooltip || !featureLayer.getTooltip()) return
        const html = buildTooltipHTML(featureLayer, layerConfig)
        featureLayer.setTooltipContent(html)
      })
    })
  }, [i18n.language])

  useEffect(() => {
    const handleLanguageChange = () => {
      Object.values(groupRefs).forEach(gRef => {
        if (gRef?.timeDimensionControl) {
          gRef.timeDimensionControl.refreshDateDisplay()
        }
      })
    }

    i18n.on("languageChanged", handleLanguageChange)
    return () => i18n.off("languageChanged", handleLanguageChange)
  }, [i18n.language, groupRefs])

  const mapDidMount = (mapInstance, mapMount) => {
    mapMount(mapInstance)
    setMapRef(mapInstance)
  }

  const mapMountFeatureGroup = async (groupRef, layer, isTimeDimensionLayer) => {
    let gRef = groupRef

    if (isTimeDimensionLayer) {
      createTimeDimensionLayer(mapRef, gRef, layer, config, (updatedGroupRef) => {
        setGroupRefs(prevState => ({
          ...prevState,
          [layer.key]: updatedGroupRef
        }))
        props.mapMountFeatureGroup(updatedGroupRef, layer)
      }, dispatch, i18n, accountinfo?.token)

      return
    }

    let clusterGroupRef = groupRefs[groupRef.key]?.clusterGroupRef

    if (layer.cluster) {
      const paneKey = layer.pane ? layer.pane : `${layer.key}Pane`

      const clusterGroupOptions = {
        showCoverageOnHover: true,
        zoomToBoundsOnClick: false,
        spiderfyOnMaxZoom: false,
        animate: false,
        animateAddingMarkers: false,
        maxClusterRadius: 80,
        pane: paneKey,
        clusterPane: paneKey,
        spiderLegPolylineOptions: {
          weight: 2,
          color: "#212121",
          opacity: 0.6
        },
        polygonOptions: {
          pane: `clusterPolygon-${paneKey}`,
          fillColor: theme.palette.primary.main,
          fillOpacity: 0.4,
          color: theme.palette.primary.dark,
          opacity: 1,
          weight: 2
        },
        ...(layer.clusterOptions || {})
      }

      if (!clusterGroupRef) {
        clusterGroupRef = L.markerClusterGroup(clusterGroupOptions)
        clusterGroupRef.key = layer.key

        clusterGroupRef.on("clusterclick", function (e) {
          e.propagatedFrom.spiderfy()
        })

        mapRef.addLayer(clusterGroupRef)
      } else {
        clusterGroupRef.clearLayers()
      }

      const markers = []
      layer.features.forEach(feature => {
        const latlng = L.latLng(
          feature.geometry.coordinates[1],
          feature.geometry.coordinates[0]
        )

        const marker = pointToLayer(feature, latlng, layer)
        marker.feature = feature

        onEachFeature(marker, layer)

        marker.on("click", (event) => {
          let featureLayer = event.target
          featureLayer.key = layer.key

          mapFeatureClick && mapFeatureClick(featureLayer, layer)
        })

        markers.push(marker)
      })
      clusterGroupRef.addLayers(markers)

      gRef.clusterGroupRef = clusterGroupRef
    } else {
      if (clusterGroupRef) {
        clusterGroupRef.clearLayers()
        clusterGroupRef.removeFrom(mapRef)
      }
    }

    setGroupRefs(prevState => ({
      ...prevState,
      [layer.key]: gRef
    }))

    props.mapMountFeatureGroup(gRef, layer)
  }

  const fetchFeatures = async (layer, callback) => {
    if (Boolean(layer.url) && layer.wms !== true && layer.cog !== true) {
      const loaderValue = (layer.checked ? 1 : 0)
      dispatch(incrementByAmount(loaderValue))

      try {
        let url = layer.url?.url || layer.url
        if (layer.url.embedParamsInUrl) {
          url += `&parameters=${encodeURIComponent(JSON.stringify(layer.url.parameters))}`
        } else {
          Object.keys(layer.url.parameters).forEach(key => {
            url += `&${key}=${layer.url.parameters[key]}`
          })
        }

        const response = await makeRequest(url, `fetchFeatures - ${layer.name}`, "GET", controllerRef.current.signal, layer.url?.parameters, layer.url?.body)

        dispatch(decrementByAmount(loaderValue))

        // Pass the whole response so catchMessage still sees the HTTP status. Anything
        // that is not a string is a failure object, never parseable features.
        if (typeof response !== "string" || response?.exception) {
          dispatch(catchMessage(response, `fetchFeatures - ${layer.name}`))
          if (callback) {
            callback()
          }
          return
        }

        let json
        try {
          json = JSON.parse(response)
        } catch (error) {
          // A 200 can still carry an exception report instead of GeoJSON
          throw new Error(describeResponseBody(response) || error.message)
        }

        const features = Array.isArray(json) ? json : json?.features || []

        config.layers.find(l => l.key === layer.key).features = features

        if (layer.url?.hasOwnProperty("callback")) {
          layer.url.callback(JSON.parse(JSON.stringify(features)))
        }

        if (callback) {
          callback()
        }
      } catch (error) {
        dispatch(decrementByAmount(loaderValue))
        dispatch(catchMessage({ exception: error }, `fetchFeatures - ${layer.name}`))
        if (callback) {
          callback()
        }
      }
    }
  }

  const getPadding = (placement) => {
    let padding = [0, 0]
    const drawerEl = document.getElementById(`drawer-${placement}`)

    if (drawerEl) {
      const offset = drawerEl.style.visibility !== "hidden"
        ? drawerEl.offsetWidth
        : 0

      padding[0] = offset
    }
    return padding
  }

  const buildTooltipHTML = (featureLayer, layer) => {
    let tooltip = ""

    if (layer.tooltip.name) {
      tooltip += `<b style="font-size: 14px;">${t(layer.tooltip.name)}</b><br />`
    }

    if (layer.tooltip.layer === true) {
      tooltip += `<b>${t("layer")}:</b> ${layer.name}<br />`
    }

    if (layer.tooltip.fields) {
      layer.tooltip.fields.forEach((field) => {
        let val = featureLayer.feature.properties[field.field]
        if (field.formatter) val = field.formatter(val)
        tooltip += `<b>${t(field.name)}:</b> ${val === null || val === undefined ? "-" : val}${field.unit ? ` ${field.unit}` : ""}<br />`
      })
    }

    if (layer.tooltip.allFields) {
      Object.entries(featureLayer.feature.properties).forEach(([key, value]) => {
        if (["color"].includes(key)) return
        const label = capitalizeFirstLetter(key.toLowerCase().replaceAll("_", " ").trim())
        tooltip += `<b>${t(label)}:</b> ${value}<br />`
      })
    }

    return tooltip
  }

  const onEachFeature = (featureLayer, layer) => {
    if (layer.tooltip) {
      featureLayer.bindTooltip(buildTooltipHTML(featureLayer, layer), {
        interactive: false,
        sticky: Boolean(layer.tooltip?.sticky)
      })
    }

    if (featureLayer.setIcon && mapFeatureOptions) {
      mapFeatureOptions(layer, featureLayer, layer.pointerType)
    }
  }

  const pointToLayer = (feature, latlng, layer) => {
    let paneKey = layer.pane ? layer.pane : `${layer.key}Pane`

    let interactive = true
    if (feature.properties.isparameter === false) {
      interactive = false
      paneKey = `${paneKey}NoData`
    }

    const options = {
      pane: paneKey,
      shadowPane: paneKey,
      feature: feature,
      ...layer.options || {},
      interactive
    }

    const isCircle = ["circle", "radius"].includes(layer.pointerType)

    let marker
    if (isCircle) {
      marker = L.circleMarker(latlng, options)
    } else {
      marker = L.marker(latlng, options)
    }

    if (layer.cluster && layer.popup) {
      marker.bindPopup("", {
        closeButton: false,
        minWidth: 460,
        maxWidth: 460,
        autoPan: false
      })
        .on("popupopen", (event) => {
          const container = document.createElement("div")

          createRoot(container).render(<>
            {renderPopupHeader(layer)}
            {renderPopupContent(layer, { feature })}
          </>)

          marker.setPopupContent(container)

          const ref = {
            ...event.popup,
            open: true
          }
          popupElRef.current = ref

          setSelectedLayer({
            ...event.target,
            key: layer.key
          })
          checkMinimizedStyling()
        })
        .on("popupclose", () => {
          popupElRef.current.open = false
          setSelectedLayer()
        })
    }

    return marker
  }

  const renderPopupHeader = (layer) => {
    return (
      <div className="popup-header">
        <Typography variant="h6">{layer.name}</Typography>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Tooltip
            title={dockedRef.current ? t("Koppelen") : t("Loskoppelen")}
            disableInteractive
            slotProps={{
              tooltip: {
                id: "docked-tooltip"
              }
            }}
            onOpen={() => {
              setTimeout(() => {
                const el = document.getElementById("docked-tooltip")
                if (el) {
                  el.innerHTML = dockedRef.current ? t("Koppelen") : t("Loskoppelen")
                }
              }, 50)
            }}
          >
            <IconButton
              size="small"
              sx={{ cursor: "pointer", marginRight: theme.spacing(0.5), padding: theme.spacing(0.5) }}
              onClick={() => {
                setDocked(!dockedRef.current)
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24" height="24"
                fill="currentColor"
                className="mi-solid mi-window-dock-undock"
                viewBox="0 0 24 24"
              >
                <path d="M7 4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h14c.55 0 1-.45 1-1V5c0-.55-.45-1-1-1zm13 10H8V7h12zm-4 4H4V8H3c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h14c.55 0 1-.45 1-1v-1z" />
              </svg>
            </IconButton>
          </Tooltip>
          <Tooltip
            title={minimizedRef.current ? t("Uitklappen") : t("Inklappen")}
            disableInteractive
            slotProps={{
              tooltip: {
                id: "minimized-tooltip"
              }
            }}
            onOpen={() => {
              setTimeout(() => {
                const el = document.getElementById("minimized-tooltip")
                if (el) {
                  el.innerHTML = minimizedRef.current ? t("Uitklappen") : t("Inklappen")
                }
              }, 50)
            }}
          >
            <IconButton
              size="small"
              onClick={() => setMinimized(!minimizedRef.current)}
              sx={{ marginRight: theme.spacing(0.5), padding: theme.spacing(0.5) }}
            >
              <UnfoldMoreRounded
                id="minimized"
                sx={{ display: minimizedRef.current ? "block" : "none" }}
              />
              <UnfoldLessRounded
                id="maximized"
                sx={{ display: !minimizedRef.current ? "block" : "none" }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={t("Sluiten")}
            disableInteractive
          >
            <IconButton
              size="small"
              sx={{ cursor: "pointer", padding: theme.spacing(0.5) }}
              onClick={() => {
                if (!popupElRef.current) return
                mapRef.closePopup()
              }}
            >
              <CloseRounded />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    )
  }

  const renderPopupContent = (layer, featureLayer) => {
    const selectedFeatureLayer = featureLayer || selectedLayer

    if (!selectedFeatureLayer) return

    const fields = layer.popup?.fields
      ? layer.popup.fields.map((field) => {
        let val = selectedFeatureLayer.feature.properties[field.field]

        if (field.formatter) {
          val = field.formatter(val)
        }

        return {
          ...field,
          value: val
        }
      })
      : (Object.entries(selectedFeatureLayer.feature.properties).map(([key, value]) => {
        if (["color"].includes(key)) return
        const label = capitalizeFirstLetter(key.toLowerCase().replaceAll("_", " ").trim())
        return { name: label, value: value }
      })).filter(Boolean)

    return (
      <div className="popup-content">
        {layer.popup?.propertiesTable !== false
          ? <table>
            <tbody>
              {fields.map((field, index) => {
                return (
                  <tr key={index}>
                    <td>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2">{field.name}</Typography>
                        {field.description && field.description !== field.name
                          ?
                          <Tooltip title={field.description} disableInteractive>
                            <HelpOutlineRounded color="warning" fontSize="small" sx={{ marginLeft: theme.spacing(1) }} />
                          </Tooltip>
                          : null
                        }
                      </div>
                    </td>
                    <td>
                      <Typography variant="body2">
                        {Array.isArray(field.value)
                          ? field.value.map((val) => val).join(", ")
                          : field.value === null || field.value === undefined ? "-" : field.value
                        }
                        {field.unit ? field.unit : ""}
                      </Typography>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          : null
        }
        {layer.popup?.renderContent
          ? layer.popup.renderContent(selectedFeatureLayer)
          : null}
      </div>
    )
  }

  const renderControl = (control, index) => {
    if (!mapRef) return null


    switch (control.name) {
      case "LayersControl":
        return (
          <LayersControl
            key={index}
            position={control.position}
          >
            {hovered && (
              <GeoJSON
                key={`hovered-${selected.id}`}
                key={hovered.id}
                data={hovered.feature}
                pane="highlight"
                interactive={false}
                style={(feature) => mapFeatureStyle && mapFeatureStyle(hovered.layer, { feature: feature }, true)}
              />
            )}
            {selected && (
              <GeoJSON
                key={`selected-${selected.id}`}
                data={selected.feature}
                pane="selected"
                interactive={false}
                style={(feature) => mapFeatureStyle && mapFeatureStyle(selected.layer, { feature: feature }, false)}
              />
            )}
            {config.baseLayers?.map((baseLayer, index) => {
              return (
                <BaseLayer key={index} checked={baseLayer.checked} name={t(baseLayer.name)}>
                  <TileLayer
                    url={baseLayer.url}
                    maxNativeZoom={20}
                    maxZoom={22}
                  />
                </BaseLayer>
              )
            })}
            {config.layers?.map((layer, index) => {
              if (layer.disabled || layer.inactive) return

              const paneKey = layer.pane ? layer.pane : `${layer.key}Pane`
              if (!panes.find(pane => pane.key === paneKey)) return

              const checked = layer.checked
                && (layer.view
                  ? Array.isArray(layer.view)
                    ? layer.view.includes(view)
                    : layer.view === view
                  : true)

              if (layer.wms) {
                return (
                  <Overlay
                    key={index}
                    checked={checked}
                    name={layer.name}
                  >
                    <WMSTileLayer
                      key={layer.layer}
                      checked={checked}
                      pane={paneKey}
                      layers={layer.layer}
                      styles={layer.styles}
                      opacity={layer.options && layer.options.opacity}
                      url={`${layer.url}?tiled=true`}
                      transparent
                      format="image/png"
                      bounds={layer.bounds && layer.bounds}
                      maxNativeZoom={20}
                      maxZoom={22}
                      eventHandlers={{
                        add: (event) => {
                          event.target.name = layer.name
                          event.target.key = layer.key
                          mapMountFeatureGroup(event.target, layer)
                        },
                        remove: (event) => {
                          mapRemoveFeatureGroup(layer.key)
                        }
                      }}
                    />
                  </Overlay>
                )
              }

              geoJsonRefs.current[layer.key] = null

              return (
                <Overlay
                  key={index}
                  checked={checked}
                  name={layer.name}
                >
                  <GeoJSON
                    ref={(el) => (geoJsonRefs.current[layer.key] = el)}
                    key={JSON.stringify(layer.features)}
                    pane={paneKey}
                    data={layer.cluster
                      ? []
                      : layer.features || []
                    }
                    markersInheritOptions
                    interactive={layer.interactive === false ? false : true}

                    style={(feature) => mapFeatureStyle && mapFeatureStyle(layer, { feature: feature })}

                    eventHandlers={{
                      add: (e) => {
                        e.target.name = layer.name
                        e.target.key = layer.key
                        mapMountFeatureGroup(e.target, layer)

                        const zoomTo = layer?.zoomTo
                        const shouldZoom = zoomTo === true ||
                          (typeof zoomTo === "object" &&
                            zoomTo !== null &&
                            (zoomTo.state === undefined || zoomTo.state === true))

                        if (shouldZoom) {
                          const bounds = e.target.getBounds()
                          if (bounds.isValid()) {
                            mapRef.fitBounds(bounds, {
                              paddingTopLeft: layer.zoomTo?.padding || getPadding("left"),
                              paddingBottomRight: layer.zoomTo?.padding || getPadding("right"),
                              maxZoom: layer.zoomTo?.maxZoom
                            })
                          }
                        }
                      },
                      remove: () => {
                        const groupRef = groupRefs[layer.key]
                        const clusterGroupRef = groupRef.clusterGroupRef

                        if (clusterGroupRef) {
                          clusterGroupRef.removeFrom(mapRef)
                          clusterGroupRef.clearLayers()
                        }

                        setGroupRefs(prevState => {
                          const { [layer.key]: _, ...rest } = prevState
                          return rest
                        })

                        mapRemoveFeatureGroup(layer.key)
                      },
                      click: (e) => {
                        setSelected(selected?.id === e.propagatedFrom._leaflet_id ? null : {
                          id: e.propagatedFrom._leaflet_id,
                          feature: e.propagatedFrom.feature,
                          layer: layer
                        })

                        if (layer.popup) {
                          if (selectedLayer?._leaflet_id === e.propagatedFrom?._leaflet_id) {
                            // if (!popupElRef.current) return
                            mapRef.closePopup()
                            return
                          } else {
                            setSelectedLayer(e.propagatedFrom)
                          }
                        }

                        let featureLayer = e.propagatedFrom
                        featureLayer.key = layer.key
                        mapFeatureClick && mapFeatureClick(featureLayer, layer)
                      }
                    }}
                    onEachFeature={(feature, featureLayer) => {
                      onEachFeature(featureLayer, layer)

                      featureLayer.on({
                        mouseover: (e) => {
                          setHovered({
                            id: e.target._leaflet_id,
                            feature: e.target.feature,
                            layer: layer
                          })

                          // if (e.target.setStyle && mapFeatureStyle) {
                          //   mapFeatureStyle(layer, e.target, true)
                          // }
                          // if (e.target.setIcon && mapFeatureOptions) {
                          //   mapFeatureOptions(layer, e.target, layer.pointerType, true)
                          // }
                        },
                        mouseout: (e) => {
                          setHovered(null)

                          // if (e.target.setStyle && mapFeatureStyle) {
                          //   mapFeatureStyle(layer, e.target, false)
                          // }
                          // if (e.target.setIcon && mapFeatureOptions) {
                          //   mapFeatureOptions(layer, e.target, layer.pointerType, false)
                          // }
                          // geoJsonRefs.current[layer.key].resetStyle(e.target)
                          mapRef.eachLayer(layer => {
                            if (layer.closeTooltip) layer.closeTooltip()
                          })
                        }
                      })
                    }}
                    pointToLayer={(feature, latlng) => pointToLayer(feature, latlng, layer)}
                  >
                    {(layer.popup && layer.cluster !== true) && <Popup
                      closeButton={false}
                      minWidth={460}
                      maxWidth={460}
                      autoPan
                      eventHandlers={{
                        add: (event) => {
                          const ref = {
                            ...event.target,
                            open: true
                          }
                          popupElRef.current = ref
                        },
                        remove: () => {
                          popupElRef.current.open = false
                          setMinimized(false)
                          setSelectedLayer()
                        }
                      }}
                    >
                      {renderPopupHeader(layer)}
                      {renderPopupContent(layer)}
                    </Popup>}
                  </GeoJSON>
                </Overlay>
              )
            })}
            {config.timeDimensionLayers?.map((layer, index) => {
              const paneKey = layer.pane ? layer.pane : `${layer.key}Pane`

              const checked = layer.checked
                && (layer.view
                  ? Array.isArray(layer.view)
                    ? layer.view.includes(view)
                    : layer.view === view
                  : true)

              // claude: key mag niet louter `index` zijn — react-leaflet voegt een FeatureGroup alleen toe/
              // verwijdert 'm via de add/remove-events bij een echte mount/unmount, niet wanneer alleen de
              // inhoud van `layer` op dezelfde index verandert (bv. bij het wisselen van selectedProject).
              // Zonder een key die meeverandert met de daadwerkelijke WMS-laagnaam blijft de oude, inmiddels
              // handmatig afgebroken (destroyTimeDimensionLayer) tijdlaag "dood" hangen, want createTimeDimensionLayer
              // wordt dan nooit opnieuw aangeroepen.
              const wmsLayerName = Array.isArray(layer.layer) ? layer.layer.join(",") : layer.layer

              return (
                <Overlay
                  key={`${layer.key}-${wmsLayerName}`}
                  checked={checked}
                  name={layer.name}
                >
                  <FeatureGroup
                    pane={paneKey}
                    eventHandlers={{
                      add: (event) => {
                        event.target.name = layer.name
                        event.target.key = layer.key
                        mapMountFeatureGroup(event.target, layer, true)
                      },
                      remove: (event) => {
                        mapRemoveFeatureGroup(layer.key)
                      }
                    }}
                  />
                </Overlay>
              )
            })}
          </LayersControl>
        )

      case "ZoomControl":
        return (
          <ZoomControl
            key={`${index}-${control.position}`}
            position={control.position}
            zoomInTitle={t("zoomin")}
            zoomOutTitle={t("zoomout")}
          />
        )

      case "ScaleControl":
        return (
          <ScaleControl
            key={`${index}-${control.position}`}
            position={control.position}
            imperial={control.metric ? false : true}
            metric={control.metric ? true : false}
          />
        )

      case "ResetBoundsControl":
        return (
          <ResetBoundsControl
            key={`${index}-${control.position}`}
            bounds={props.bounds || bounds}
            position={control.position}
          />
        )

      default:
        return null
    }
  }

  return (
    <MapContainer
      attributionControl={false}

      whenReady={mapInstance => {
        const ref = mapInstance.target

        ref.key = config.key
        mapDidMount(ref, mapMount)

        ref.on("click", function (e) {
          mapClick && mapClick(e)
        })

        ref.fitBounds(props.bounds || bounds, {
          animate: false,
          paddingTopLeft: getPadding("left"),
          paddingBottomRight: getPadding("right"),
        })
        ref.setMaxBounds(maxBounds)

        attribution.addTo(ref)
      }}
      center={[config.lat, config.lng]}
      zoom={config.zoom}
      minZoom={config.minZoom}
      maxZoom={config.maxZoom}

      zoomControl={false}
      zoomSnap={0.1}
      zoomDelta={1}

      style={{ height: "100%", width: "100%" }}
    >
      {panes.map((pane, index) => (
        <Pane key={index} name={pane.key} style={{ zIndex: pane.zIndex }} />
      ))}
      {config.controls.map((control, index) => (
        renderControl(control, index)
      ))}
    </MapContainer>
  )
}

export default HKVMap